import { components } from "@/api-schema";
import {
  ModelGenerationIdentifierProvider,
  PathsV1ModelFamilySearchPostParametersQueryExtend,
  PathsV1ModelFamilySearchPostParametersQuerySelect,
  PathsV1ModelGenerationSearchPostParametersQueryExtend,
  PathsV1ModelGenerationSearchPostParametersQuerySelect,
  PathsV1ModelGenerationUuidGetParametersQueryExtend,
  VehicleKind,
} from "@/api-schema-enums";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { useModelGenerationByUuidQuery } from "@/common/api/model-generation/use-model-generation-by-uuid.query";
import { useModelGenerationCreateMutation } from "@/common/api/model-generation/use-model-generation-create.mutation";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import { useModelGenerationUpdateMutation } from "@/common/api/model-generation/use-model-generation-update.mutation";
import { getModelFamilyDatId } from "@/common/helpers/get-model-family-dat-id";
import { getModelGenerationDatId } from "@/common/helpers/get-model-generation-dat-id";
import { createPlaceholderKeys } from "@/common/helpers/list-keys";
import { normalizeGeneratedOptionalString } from "@/common/helpers/normalize-generated-optional-string";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";
import { DAT_ID_INPUT_PATTERN, sanitizeDatIdInput } from "../../_helpers/dat-id-input";
import { validateModelGenerationDuplicates } from "../_helpers/validate-model-generation-duplicates";

type ModelGeneration = components["schemas"]["ModelGeneration"];

const VALIDATION_ERROR_DURATION = 5000;
const VEHICLE_KIND_VALUES = [
  VehicleKind.UNKNOWN,
  VehicleKind.MotorVehicle,
  VehicleKind.CommercialVehicle,
  VehicleKind.Motorcycle,
  VehicleKind.Truck,
] as const;

function getNormalizedModelGenerationDatId(modelGeneration?: ModelGeneration | null): string | undefined {
  return normalizeGeneratedOptionalString(getModelGenerationDatId(modelGeneration?.modelGenerationIdentifiers));
}

export const ModelGenerationFormSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    modelFamily: z.string().uuid().min(1, t("masterdata:ModelFamilyNameIsRequired")),
    name: z.string().trim().min(1, t("masterdata:ModelNameIsRequired")),
    vehicleKind: z.enum(VEHICLE_KIND_VALUES),
    datId: z
      .string()
      .trim()
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: t("masterdata:DatIdMustBeValidPositiveNumber"),
      }),
  });

export type ModelGenerationFormSchemaType = z.infer<ReturnType<typeof ModelGenerationFormSchema>>;

interface ModelGenerationFormFieldsProps {
  uuid?: string;
}

export function ModelGenerationFormFields({ uuid }: ModelGenerationFormFieldsProps = {}) {
  const isUpdateMode = !!uuid;
  const { t, i18n } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();

  const { data: modelGeneration, isPending: isModelGenerationLoading } = useModelGenerationByUuidQuery(uuid ?? "", {
    parameters: {
      extend: [
        PathsV1ModelGenerationUuidGetParametersQueryExtend.ModelFamily,
        PathsV1ModelGenerationUuidGetParametersQueryExtend.ModelFamily_Brand,
        PathsV1ModelGenerationUuidGetParametersQueryExtend.ModelGenerationIdentifiers,
      ],
    },
  });

  const isFormReady = !isUpdateMode || !!modelGeneration;
  const initializedModelGenerationUuidRef = useRef(modelGeneration?.uuid);

  const form = useForm<ModelGenerationFormSchemaType>({
    resolver: zodResolver(ModelGenerationFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues:
      isUpdateMode && modelGeneration
        ? {
            modelFamily: modelGeneration.modelFamily?.uuid || "",
            name: modelGeneration.name || "",
            vehicleKind: modelGeneration.vehicleKind ?? VehicleKind.UNKNOWN,
            datId: getNormalizedModelGenerationDatId(modelGeneration) || "",
          }
        : {
            modelFamily: "",
            name: "",
            vehicleKind: VehicleKind.UNKNOWN,
            datId: "",
          },
  });

  useEffect(() => {
    if (!isUpdateMode || !modelGeneration || initializedModelGenerationUuidRef.current === modelGeneration.uuid) {
      return;
    }

    initializedModelGenerationUuidRef.current = modelGeneration.uuid;
    form.reset({
      modelFamily: modelGeneration.modelFamily?.uuid || "",
      name: modelGeneration.name || "",
      vehicleKind: modelGeneration.vehicleKind ?? VehicleKind.UNKNOWN,
      datId: getNormalizedModelGenerationDatId(modelGeneration) || "",
    });
  }, [form, isUpdateMode, modelGeneration]);

  const createMutation = useModelGenerationCreateMutation();
  const updateMutation = useModelGenerationUpdateMutation();

  const [watchedModelFamily, watchedName, watchedDatId, watchedVehicleKind] = useWatch({
    control: form.control,
    name: ["modelFamily", "name", "datId", "vehicleKind"],
  });

  const { data: modelFamilies, isPending: isModelFamiliesLoading } = useModelFamilySearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1ModelFamilySearchPostParametersQuerySelect.Uuid,
          PathsV1ModelFamilySearchPostParametersQuerySelect.Name,
          PathsV1ModelFamilySearchPostParametersQuerySelect.ModelFamilyIdentifiers,
          PathsV1ModelFamilySearchPostParametersQuerySelect.Brand,
        ],
        extend: [
          PathsV1ModelFamilySearchPostParametersQueryExtend.Brand,
          PathsV1ModelFamilySearchPostParametersQueryExtend.ModelFamilyIdentifiers,
        ],
      },
    },
  });

  const { data: existingModelGenerations, isPending: isExistingModelGenerationsLoading } =
    useModelGenerationSearchQuery({
      options: {
        body:
          watchedModelFamily && watchedVehicleKind
            ? {
                modelFamily: [watchedModelFamily],
                vehicleKind: [watchedVehicleKind],
              }
            : {},
        parameters: {
          pageSize: -1,
          select: [
            PathsV1ModelGenerationSearchPostParametersQuerySelect.Uuid,
            PathsV1ModelGenerationSearchPostParametersQuerySelect.Name,
            PathsV1ModelGenerationSearchPostParametersQuerySelect.VehicleKind,
            PathsV1ModelGenerationSearchPostParametersQuerySelect.ModelFamily,
            PathsV1ModelGenerationSearchPostParametersQuerySelect.ModelGenerationIdentifiers,
          ],
          extend: [
            PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily,
            PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelGenerationIdentifiers,
          ],
        },
      },
      enabled: !!watchedModelFamily && !!watchedVehicleKind,
    });

  const modelFamilyOptions = useMemo<ComboboxOption[]>(() => {
    return (
      modelFamilies?.data
        ?.map((modelFamily) => ({
          value: modelFamily.uuid,
          label: [modelFamily.name, modelFamily.brand?.name].filter(Boolean).join(" · "),
          searchText: [
            modelFamily.name,
            getModelFamilyDatId(modelFamily.modelFamilyIdentifiers),
            modelFamily.brand?.name,
          ]
            .filter(Boolean)
            .join(" "),
        }))
        .sort((leftOption, rightOption) => leftOption.label.localeCompare(rightOption.label)) ?? []
    );
  }, [modelFamilies?.data]);

  const vehicleKindOptions = useMemo(() => {
    const language = i18n.language === "de" ? "de" : "en";

    return VEHICLE_KIND_VALUES.map((value) => ({
      value,
      label: VEHICLE_KIND_LABELS[value]?.[language] ?? value,
    }));
  }, [i18n.language]);

  const duplicateValidation = useMemo(() => {
    const { hasDuplicateName, hasDuplicateDatId } = validateModelGenerationDuplicates(
      {
        uuid,
        modelFamily: watchedModelFamily,
        vehicleKind: watchedVehicleKind,
        name: watchedName,
        datId: watchedDatId,
      },
      existingModelGenerations?.data,
    );
    const errors: string[] = [];
    if (hasDuplicateName) {
      errors.push(t("masterdata:ModelNameAlreadyExists", { name: watchedName }));
    }
    if (hasDuplicateDatId) {
      errors.push(t("masterdata:ModelDatIdAlreadyExists", { datId: watchedDatId }));
    }

    return {
      errors,
      hasErrors: errors.length > 0,
      hasDuplicateDatId,
      hasDuplicateName,
    };
  }, [existingModelGenerations?.data, t, uuid, watchedDatId, watchedModelFamily, watchedName, watchedVehicleKind]);

  const handleSubmitWithValidation = async (values: ModelGenerationFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: VALIDATION_ERROR_DURATION,
        });
      });
      return;
    }

    const payload = {
      name: values.name.trim(),
      modelGenerationIdentifiers: values.datId?.trim()
        ? [
            {
              identifier: values.datId.trim(),
              provider: ModelGenerationIdentifierProvider.Dat,
            },
          ]
        : [],
      modelFamily: values.modelFamily,
      vehicleKind: values.vehicleKind,
    };

    try {
      await toastMutationStatus({
        mutationFn: () =>
          isUpdateMode && uuid
            ? updateMutation.mutateAsync({
                uuid,
                ...payload,
              })
            : createMutation.mutateAsync(payload),
        successMessage: isUpdateMode
          ? t("masterdata:ModelSuccessfullyUpdated")
          : t("masterdata:ModelSuccessfullyCreated"),
        errorMessage: isUpdateMode ? t("masterdata:ErrorUpdatingModel") : t("masterdata:ErrorCreatingModel"),
      });

      navigate("~/masterdata/model-generation");
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "creating"} model generation:`, error);
    }
  };

  const isLoading = isModelFamiliesLoading || (isUpdateMode && (isModelGenerationLoading || !isFormReady));
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isCheckingDuplicates = isExistingModelGenerationsLoading && !!watchedModelFamily;
  const isSubmitDisabled =
    isPending || isCheckingDuplicates || duplicateValidation.hasErrors || !form.formState.isValid;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {createPlaceholderKeys(3, "skeleton").map((key) => (
          <div key={key} className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="modelFamily"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("masterdata:ModelFamily")} *</FormLabel>
                  <FormControl>
                    <Combobox
                      options={modelFamilyOptions}
                      value={field.value}
                      onChange={field.onChange}
                      isLoading={isModelFamiliesLoading}
                      placeholder={t("common:SelectModelFamily")}
                      searchPlaceholder={t("common:SearchModelFamilies")}
                      emptyText={t("common:NoModelFamiliesFound")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicleKind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:VehicleKind")} *</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (value) {
                        field.onChange(value);
                      }
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("masterdata:SelectVehicleKind")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vehicleKindOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:ModelGeneration")} *</FormLabel>
                  <div className="relative">
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      placeholder={t("masterdata:ModelNameExample")}
                      error={duplicateValidation.hasDuplicateName}
                      disabled={isCheckingDuplicates}
                    />
                    {isCheckingDuplicates && (
                      <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                      </div>
                    )}
                  </div>
                  <FormMessage />
                  {isCheckingDuplicates ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateModelNames")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateName && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:ModelNameDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="datId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:ModelGenerationDatId")}</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        placeholder={t("masterdata:DatIdExample")}
                        inputMode="numeric"
                        pattern={DAT_ID_INPUT_PATTERN}
                        {...field}
                        value={field.value ?? ""}
                        onChange={(event) => field.onChange(sanitizeDatIdInput(event.target.value))}
                        error={duplicateValidation.hasDuplicateDatId}
                        disabled={isCheckingDuplicates}
                      />
                    </FormControl>
                    {isCheckingDuplicates && (
                      <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                      </div>
                    )}
                  </div>
                  <FormMessage />
                  {isCheckingDuplicates ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateDatIds")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateDatId && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:ModelDatIdDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            {isUpdateMode && modelGeneration && (
              <SystemInfoFields
                uuid={modelGeneration.uuid}
                createdAt={modelGeneration.createdAt}
                updatedAt={modelGeneration.updatedAt}
              />
            )}
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={isUpdateMode ? t("common:SaveChanges") : t("common:Save")}
        />
      </form>
    </Form>
  );
}
